import React, { useState, useEffect } from "react";
import { auth, db, checkIsAdmin, getUserByUsername } from "../firebase/config";
import {
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  getAuth,
} from "firebase/auth";
import { collection, query, where, getDocs } from "firebase/firestore";
import { useNavigate, Link } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { toast } from "react-toastify";

const Login = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [user] = useAuthState(auth);

  // Kullanıcı zaten giriş yapmışsa ana sayfaya yönlendir
  useEffect(() => {
    if (user) {
      navigate("/", { replace: true });
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(""); // Reset error

    try {
      // Önce username'e göre kullanıcıyı bul
      const user = await getUserByUsername(username);

      if (!user) {
        setError("Invalid username or password");
        setLoading(false);
        return;
      }

      // Kullanıcının email'i ile giriş yap
      const auth = getAuth();
      await signInWithEmailAndPassword(auth, user.email, password);

      // Admin kontrolü
      const isAdmin = await checkIsAdmin(user.uid);

      toast.success("Login successful!");

      // Yönlendirme
      if (isAdmin) {
        navigate("/admin");
      } else {
        navigate("/");
      }
    } catch (error) {
      console.error("Login error:", error);
      setError("Invalid username or password");
      toast.error("Login failed. Please check your credentials.");
    }

    setLoading(false);
  };

  const handleForgotPassword = async () => {
    if (!username) {
      toast.error("Please enter your username");
      return;
    }

    try {
      // Find email corresponding to username
      const usersRef = collection(db, "users");
      const q = query(usersRef, where("username", "==", username));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        toast.error("Can't find username");
        return;
      }

      const userDoc = querySnapshot.docs[0];
      const email = userDoc.data().email;

      // Send password reset email
      await sendPasswordResetEmail(auth, email);
      toast.success("Password reset email sent");
    } catch (error) {
      toast.error("An error occurred. Please try again.");
      console.error(error);
    }
  };

  return (
    <div className='min-h-screen bg-[#F8F9FA] flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8'>
      <div className='max-w-md w-full bg-white rounded-lg shadow-lg p-8'>
        <div className='text-center mb-8'>
          <h2 className='text-3xl font-semibold text-gray-800'>Login</h2>
        </div>

        {error && (
          <div className='bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-6 text-base font-medium'>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className='space-y-6'>
          <div>
            <label className='block text-gray-700 font-medium text-base mb-2'>
              Username
            </label>
            <input
              type='text'
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              className='w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base'
            />
          </div>

          <div>
            <label className='block text-gray-700 font-medium text-base mb-2'>
              Password
            </label>
            <input
              type='password'
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className='w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base'
            />
          </div>

          <div className='flex items-center justify-between'>
            <div className='text-sm'>
              <Link
                to='/forgot-password'
                className='font-medium text-blue-600 hover:text-blue-700'>
                Forgot Password
              </Link>
            </div>
            <div className='text-sm'>
              <Link
                to='/register'
                className='font-medium text-blue-600 hover:text-blue-700'>
                Don't have an account? Register
              </Link>
            </div>
          </div>

          <div>
            <button
              type='submit'
              disabled={loading}
              className='w-full py-3 px-4 border border-transparent rounded-lg shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 font-medium text-lg disabled:opacity-50'>
              {loading ? "Logging in..." : "Login"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login;
